// src/runtime/scenes.js
// Environment Scenes: living, cinematic atmosphere layers for crusher-ui-kit.
//
// A scene is an OPT-IN background world: a palette + ambient particle FX. It layers OVER the
// existing theme (data-theme) and mode (data-mode), never replacing them. Consumers theme their
// product WITH a scene; scenes never impose an identity. Named consumer today: life-os, which
// will delete its bespoke weather FX and consume this once it migrates.
//
// Contract (per docs/planning/18-environment-scenes.md):
//   setScene(name, opts?)                     - activate a scene by name; '' / null clears it
//   setSceneInputs({ weather, intensity })    - live-tune the active scene
//   <crusher-scene name="forest">             - declarative element form
//
// Non-negotiables: opt-in only, prefers-reduced-motion kills the FX layer, particle budgets are
// capped, pointer-events:none so it never blocks UI, z-index:-1 so content always wins.

const HAS_BROWSER = typeof window !== 'undefined' && typeof document !== 'undefined';
const REDUCED =
  HAS_BROWSER && typeof matchMedia !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Deterministic pseudo-random so a scene looks the same every render (no jitter on re-layout).
function seeded(n, salt) {
  const x = Math.sin(n * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

// Each FX type has a particle budget and a render function returning one particle element.
const FX = {
  rain: { budget: 90, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `left:${(seeded(i, 1) * 100).toFixed(2)}%;animation-delay:${(-seeded(i, 2) * 1.4).toFixed(2)}s;animation-duration:${(0.5 + seeded(i, 3) * 0.5).toFixed(2)}s;height:${(12 + seeded(i, 4) * 16).toFixed(0)}px;opacity:${(0.3 + seeded(i, 5) * 0.4).toFixed(2)}`;
    return el;
  } },
  snow: { budget: 70, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `left:${(seeded(i, 1) * 100).toFixed(2)}%;animation-delay:${(-seeded(i, 2) * 6).toFixed(2)}s;animation-duration:${(5 + seeded(i, 3) * 6).toFixed(2)}s;--sway:${(20 + seeded(i, 4) * 40).toFixed(0)}px;opacity:${(0.5 + seeded(i, 5) * 0.5).toFixed(2)}`;
    return el;
  } },
  clouds: { budget: 6, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `top:${(seeded(i, 1) * 40).toFixed(0)}%;width:${(220 + seeded(i, 2) * 320).toFixed(0)}px;height:${(70 + seeded(i, 3) * 80).toFixed(0)}px;animation-delay:${(-seeded(i, 4) * 60).toFixed(0)}s;animation-duration:${(70 + seeded(i, 5) * 80).toFixed(0)}s;opacity:${(0.18 + seeded(i, 6) * 0.18).toFixed(2)}`;
    return el;
  } },
  wind: { budget: 14, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `top:${(seeded(i, 1) * 100).toFixed(0)}%;width:${(80 + seeded(i, 2) * 160).toFixed(0)}px;animation-delay:${(-seeded(i, 3) * 4).toFixed(2)}s;animation-duration:${(2.5 + seeded(i, 4) * 3).toFixed(2)}s;opacity:${(0.08 + seeded(i, 5) * 0.12).toFixed(2)}`;
    return el;
  } },
  leaves: { budget: 22, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `left:${(seeded(i, 1) * 100).toFixed(2)}%;animation-delay:${(-seeded(i, 2) * 9).toFixed(2)}s;animation-duration:${(7 + seeded(i, 3) * 8).toFixed(2)}s;--spin:${(120 + seeded(i, 4) * 240).toFixed(0)}deg;opacity:${(0.5 + seeded(i, 5) * 0.4).toFixed(2)}`;
    return el;
  } },
  stars: { budget: 120, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `left:${(seeded(i, 1) * 100).toFixed(2)}%;top:${(seeded(i, 2) * 70).toFixed(2)}%;animation-delay:${(-seeded(i, 3) * 4).toFixed(2)}s;animation-duration:${(2 + seeded(i, 4) * 4).toFixed(2)}s;opacity:${(0.2 + seeded(i, 5) * 0.7).toFixed(2)}`;
    return el;
  } },
  mist: { budget: 5, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `top:${(40 + seeded(i, 1) * 50).toFixed(0)}%;width:${(300 + seeded(i, 2) * 400).toFixed(0)}px;height:${(120 + seeded(i, 3) * 120).toFixed(0)}px;animation-delay:${(-seeded(i, 4) * 30).toFixed(0)}s;animation-duration:${(40 + seeded(i, 5) * 40).toFixed(0)}s;opacity:${(0.12 + seeded(i, 6) * 0.16).toFixed(2)}`;
    return el;
  } },
  embers: { budget: 26, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `left:${(seeded(i, 1) * 100).toFixed(2)}%;animation-delay:${(-seeded(i, 2) * 7).toFixed(2)}s;animation-duration:${(5 + seeded(i, 3) * 6).toFixed(2)}s;opacity:${(0.4 + seeded(i, 4) * 0.5).toFixed(2)}`;
    return el;
  } },
  // The life-os reference port: rain meets the floor with splash ripples, snow
  // slowly accumulates into a capped drift, storms flash with lightning.
  // Ported from its bespoke layer so it can delete that layer entirely.
  splash: { budget: 16, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `left:${(2 + seeded(i, 9) * 96).toFixed(2)}%;animation-delay:${(-seeded(i, 10) * 1.6).toFixed(2)}s;animation-duration:${(1.2 + seeded(i, 11) * 0.9).toFixed(2)}s`;
    return el;
  } },
  drift: { budget: 1, render: () => {
    const el = document.createElement('i');
    el.style.cssText = 'animation-duration:480s'; // accumulation pace; the cap lives in the keyframe
    return el;
  } },
  flash: { budget: 2, render: (i) => {
    const el = document.createElement('i');
    el.style.cssText = `animation-delay:${(i === 0 ? 4 : 9.5).toFixed(1)}s;animation-duration:${i === 0 ? 11 : 17}s`;
    return el;
  } },
};

// The scene catalog. Each: the FX layers it runs + a one-line mood. Palettes/backgrounds live in
// scenes.css keyed by data-scene="<name>", so non-CSS consumers can ignore the visuals.
const SCENES = {
  sunny:     { fx: ['clouds', 'wind'], mood: 'Bright, airy, high-noon clarity' },
  cloudy:    { fx: ['clouds'], mood: 'Soft grey overcast, slow cover' },
  windy:     { fx: ['wind'], mood: 'Open air, gusts and streaks' },
  rain:      { fx: ['rain', 'splash', 'mist', 'clouds'], mood: 'Steady rain, splashing at the floor' },
  storm:     { fx: ['rain', 'splash', 'flash', 'mist'], mood: 'Heavy rain, lightning at the edges' },
  snow:      { fx: ['snow', 'drift', 'mist'], mood: 'Quiet snowfall, slowly gathering' },
  forest:    { fx: ['leaves', 'mist', 'wind'], mood: 'Dappled green, birdsong quiet' },
  desert:    { fx: ['wind', 'mist'], mood: 'Heat haze over warm dunes' },
  mountains: { fx: ['clouds', 'wind', 'snow'], mood: 'Thin air, distant peaks, drifting snow' },
  ocean:     { fx: ['wind', 'mist'], mood: 'Sea breeze, deep cool blue' },
  river:     { fx: ['mist', 'wind'], mood: 'Moving water, morning fog' },
  space:     { fx: ['stars', 'embers'], mood: 'Deep dark, slow drift of distant light' },
  dawn:      { fx: ['mist', 'clouds'], mood: 'First warm light, low and golden' },
  dusk:      { fx: ['embers', 'mist', 'wind'], mood: 'Last light, ember sky, cooling' },
};

let active = '';
let fxHost = null;

function ensureHost() {
  if (fxHost || !HAS_BROWSER) return fxHost;
  let host = document.querySelector('.crusher-scene-fx');
  if (!host) {
    host = document.createElement('div');
    host.className = 'crusher-scene-fx';
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
  }
  fxHost = host;
  return host;
}

function renderFx(layers, intensity = 1) {
  const host = ensureHost();
  if (!host) return;
  host.replaceChildren(); // safe clear; particles are all createElement-built below
  if (REDUCED || !layers || !layers.length) return;
  const scale = Math.min(1.5, Math.max(0.3, intensity));
  for (const kind of layers) {
    const def = FX[kind];
    if (!def) continue;
    const wrap = document.createElement('span');
    wrap.className = `fx fx-${kind}`;
    const n = Math.max(6, Math.round(def.budget * scale));
    for (let i = 0; i < n; i++) wrap.appendChild(def.render(i));
    host.appendChild(wrap);
  }
}

export function setScene(name, opts = {}) {
  if (!HAS_BROWSER) return;
  const html = document.documentElement;
  active = name && SCENES[name] ? name : '';
  if (active) {
    html.setAttribute('data-scene', active);
    renderFx(SCENES[active].fx, opts.intensity);
  } else {
    html.removeAttribute('data-scene');
    const host = ensureHost();
    if (host) host.replaceChildren();
  }
  window.dispatchEvent(new CustomEvent('crusher:scenechange', { detail: { scene: active }, bubbles: true }));
}

export function setSceneInputs({ weather, intensity } = {}) {
  if (!active) return;
  if (weather && SCENES[weather]) {
    active = weather;
    document.documentElement.setAttribute('data-scene', active);
  }
  if (typeof intensity === 'number') renderFx(SCENES[active]?.fx || [], intensity);
}

export function listScenes() {
  return Object.entries(SCENES).map(([name, def]) => ({ name, ...def }));
}

// Declarative element form: <crusher-scene name="forest"></crusher-scene>
if (HAS_BROWSER && !customElements.get('crusher-scene')) {
  class CrusherScene extends HTMLElement {
    connectedCallback() {
      if (this.isConnected) {
        setScene(this.getAttribute('name') || '', { intensity: Number(this.getAttribute('intensity')) || 1 });
      }
    }
    static get observedAttributes() { return ['name', 'intensity']; }
    attributeChangedCallback(nameAttr) {
      if (nameAttr === 'name' || nameAttr === 'intensity') {
        setScene(this.getAttribute('name') || '', { intensity: Number(this.getAttribute('intensity')) || 1 });
      }
    }
  }
  customElements.define('crusher-scene', CrusherScene);
}

export default { setScene, setSceneInputs, listScenes };
